import {
  ArrowRight,
  Clock3,
  Inbox,
  MailCheck,
  Plus,
  Send,
  Sparkles,
  UsersRound
} from "lucide-react";
import type { Dashboard, EmailSummary, PageKey, User } from "../types";
import { formatRelative, senderName } from "../lib/format";
import { Avatar, Badge, Button, Loading } from "../components/ui";

function RecentRow({
  message,
  onOpen
}: {
  message: EmailSummary;
  onOpen: (message: EmailSummary) => void;
}) {
  const person = senderName(message.direction === "Sent" ? message.toAddresses : message.fromAddress);
  return (
    <button className="recent-row" onClick={() => onOpen(message)}>
      <Avatar name={person} size="sm" tone={message.subject.length} />
      <span className="recent-person">
        <strong>{person}</strong>
        <small>{message.accountName}</small>
      </span>
      <span className="recent-subject">
        <strong>{message.subject}</strong>
        <small>{message.preview}</small>
      </span>
      <Badge tone={message.direction === "Sent" ? "violet" : "info"}>{message.direction}</Badge>
      <time>{formatRelative(message.occurredAt)}</time>
    </button>
  );
}

export default function DashboardPage({
  data,
  user,
  onCompose,
  navigate,
  openMessage
}: {
  data: Dashboard | null;
  user: User;
  onCompose: () => void;
  navigate: (page: PageKey) => void;
  openMessage: (message: EmailSummary) => void;
}) {
  if (!data) return <Loading label="Preparing your workspace…" />;
  const firstName = user.firstName;
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 18 ? "Good afternoon" : "Good evening";
  const overview = new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric" }).format(now);
  return (
    <div className="page dashboard-page">
      <div className="page-heading dashboard-heading">
        <div>
          <span className="eyebrow">{overview}</span>
          <h1>{greeting}, {firstName}</h1>
          <p>Here’s what’s moving across your shared mailboxes.</p>
        </div>
        <Button icon={<Plus size={17} />} onClick={onCompose}>
          New message
        </Button>
      </div>

      <section className="metric-grid">
        <button className="metric-card accent-blue" onClick={() => navigate("inbox")}>
          <span className="metric-icon">
            <Inbox size={20} />
          </span>
          <span>
            <small>Unread messages</small>
            <strong>{data.unreadCount}</strong>
          </span>
          <span className="metric-foot">{data.inboxCount} total in inbox</span>
        </button>
        <button className="metric-card accent-violet" onClick={() => navigate("sent")}>
          <span className="metric-icon">
            <Send size={20} />
          </span>
          <span>
            <small>Sent this week</small>
            <strong>{data.sentThisWeek}</strong>
          </span>
          <span className="metric-foot">Across all accounts</span>
        </button>
        <div className="metric-card accent-teal">
          <span className="metric-icon">
            <Clock3 size={20} />
          </span>
          <span>
            <small>Avg. response</small>
            <strong>{data.averageResponseHours}h</strong>
          </span>
          <span className="metric-foot">Measured from reply activity</span>
        </div>
        <button className="metric-card accent-amber" onClick={() => navigate("accounts")}>
          <span className="metric-icon">
            <MailCheck size={20} />
          </span>
          <span>
            <small>Connected accounts</small>
            <strong>{data.connectedAccounts}</strong>
          </span>
          <span className="metric-foot">All systems ready</span>
        </button>
      </section>

      <div className="dashboard-columns">
        <section className="panel recent-panel">
          <header className="panel-header">
            <div>
              <h2>Recent conversations</h2>
              <p>Latest movement across every mailbox you can access.</p>
            </div>
            <button className="text-link" onClick={() => navigate("inbox")}>
              View inbox <ArrowRight size={15} />
            </button>
          </header>
          <div className="recent-list">
            {data.recentMessages.map((message) => (
              <RecentRow key={message.id} message={message} onOpen={openMessage} />
            ))}
          </div>
        </section>

        <aside className="dashboard-side">
          <section className="panel quick-actions">
            <header className="panel-header">
              <div>
                <h2>Quick actions</h2>
                <p>Jump back into your work.</p>
              </div>
            </header>
            <button onClick={onCompose}>
              <span className="quick-icon violet">
                <Plus size={18} />
              </span>
              <span>
                <strong>Compose an email</strong>
                <small>Send from an assigned account</small>
              </span>
              <ArrowRight size={16} />
            </button>
            <button onClick={() => navigate("templates")}>
              <span className="quick-icon amber">
                <Sparkles size={18} />
              </span>
              <span>
                <strong>Use a template</strong>
                <small>{data.templates} polished starting points</small>
              </span>
              <ArrowRight size={16} />
            </button>
            {user.isAdministrator && (
              <button onClick={() => navigate("team")}>
                <span className="quick-icon teal">
                  <UsersRound size={18} />
                </span>
                <span>
                  <strong>Manage your team</strong>
                  <small>{data.activeUsers} active users</small>
                </span>
                <ArrowRight size={16} />
              </button>
            )}
          </section>
          <section className="panel account-status">
            <header className="panel-header">
              <div>
                <h2>Mailbox health</h2>
                <p>Last synchronization status.</p>
              </div>
            </header>
            {data.accounts.map((account) => (
              <div className="status-row" key={account.id}>
                <span className="status-dot" />
                <span>
                  <strong>{account.displayName}</strong>
                  <small>{account.emailAddress}</small>
                </span>
                <Badge tone="success">{account.connectionStatus}</Badge>
              </div>
            ))}
          </section>
        </aside>
      </div>
    </div>
  );
}
